import * as THREE from 'three';
import { SAFE_ZONE_VERTEX_CHUNK, safeZoneUniforms } from '../../shaders/safeZone.glsl.js';
import { MIXDECK_BUS, MIXDECK_BG, MIXDECK_QUAD } from '../mixdeck.js';
import { FIGURE_SDF_CHUNK, JOINTS } from './stage-figures.js';

// THE LED SCREENS: a wide central screen, two narrower side screens turned
// toward the audience, a thin LED band under the stage's lip. Their content
// is real VJ material:
//   - Mixdeck's generators (tunnel, plasma, rays, nebula) and two of our own
//     (a giant spectrum, neon shapes that hit on the beat), rendered into one
//     wide content target that spans the three screens (the side screens can
//     show its continuation - one image over three - or a mirror of the
//     centre);
//   - the COVER, sampled directly at full resolution (R18: it covers each
//     screen it is on, scale = max; sharp);
//   - the TITLE in giant letters (a canvas texture), with a countdown on the
//     build;
//   - a "camera" close-up of the DJ: the same distance function as the
//     figure on stage, framed head and shoulders.
// The LED grain is a fine mask whose contrast follows the size of a LED cell
// ON SCREEN: nothing under 3 px (no moiré, no grid), visible only in close
// shots. The screens LIGHT the stage: a pass averages their colour over 8
// zones across, which every other layer samples.

export const ZONES = 8;
export const SCREEN = {
  centre: { x: 0, y: 7.45, z: -9.6, w: 13.6, h: 7.65 },
  side: { x: 9.75, y: 7.45, z: -8.9, w: 4.6, h: 7.65, turn: 0.34 },
  band: { y: 1.34, z: 0.03, w: 24, h: 0.2 }
};
const CW = 512, CH = 168;                 // the content target, spanning the three screens
const SPAN = [5 / 24, 19 / 24];            // the centre screen's part of it

const spectrumFragment = MIXDECK_BUS + `
  void main() {
    vec2 p = stageP(vUv);
    float n = 40.0;
    float x = abs(vUv.x - 0.5) * 2.0;             // mirrored about the middle
    float i = floor(x * n);
    float k = i / n;
    float lvl = mix(mix(uBands.x, uBands.y, smoothstep(0.0, 0.5, k)), uBands.z, smoothstep(0.5, 1.0, k));
    lvl = clamp(lvl * 1.5 * (0.55 + 0.45 * vnoise(vec2(i * 1.7, uTime * 6.0))) * (0.8 + 0.5 * uPulse) + 0.12, 0.0, 1.0);
    float cell = fract(x * n);
    float bar = step(0.12, cell) * step(cell, 0.88);
    float hy = abs(vUv.y - 0.5) * 2.0;
    float on = bar * step(hy, lvl * 0.92);
    float seg = step(0.25, fract(hy * 18.0));
    vec3 c = mix(uCol0, uCol1, hy / max(lvl, 0.05)) * on * seg * 0.75;
    c += uCol2 * bar * smoothstep(0.03, 0.0, abs(hy - lvl * 0.92)) * 0.8;
    gl_FragColor = vec4(c, 1.0);
  }`;
const shapesFragment = MIXDECK_BUS + `
  float sdPoly(vec2 p, float n, float r) {
    float a = atan(p.x, p.y), k = 6.2831853 / n;
    return cos(floor(0.5 + a / k) * k - a) * length(p) - r;
  }
  void main() {
    vec2 p = stageP(vUv);
    float px = 1.0 / uRes.y;
    vec3 c = vec3(0.0);
    for (int i = 0; i < 3; i++) {
      float fi = float(i);
      float n = 3.0 + mod(floor(uBeats * 0.25) + fi, 4.0);
      float r = 0.12 + fi * 0.13 + 0.06 * uPulse;
      float a = uTime * (0.3 + 0.2 * fi) * (mod(fi, 2.0) < 1.0 ? 1.0 : -1.0);
      vec2 q = mat2(cos(a), -sin(a), sin(a), cos(a)) * (p - vec2((fi - 1.0) * 0.9, 0.0));
      float d = abs(sdPoly(q, n, r));
      vec3 col = i == 0 ? uCol0 : (i == 1 ? uCol1 : uCol2);
      c += col * (exp(-d / (3.0 * px)) * 0.9 + exp(-d / (14.0 * px)) * 0.25);
    }
    gl_FragColor = vec4(c * (0.7 + 0.5 * uPulse), 1.0);
  }`;
export const GENERATORS = ['tunnel', 'plasma', 'rays', 'nebula', 'spectrum', 'shapes'];
// The two generators of our own, for hosts that draw screens of another shape
// (shared/photoStage.js paints them into photographed LED walls). Additive export: nothing above changes.
export const STAGE_SCREEN_FRAGMENTS = { spectrum: spectrumFragment, shapes: shapesFragment };

// panel modes: x content, y cover, z title, w camera (weights)
const PANEL_CHUNK = `
  uniform sampler2D uContent;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform sampler2D uTitle;
  uniform float uTitleAsp;
  uniform sampler2D uDigits;
  uniform float uCount;          // 0 none, 1..4 the countdown digit
  uniform vec4 uModes[4];        // per panel: content, cover, title, camera
  uniform float uSideMap;        // 0 continuation, 1 mirror
  uniform vec2 uDJ[${JOINTS}];
  uniform vec3 uPal0, uPal1, uPal2;
  uniform float uTime;
  ${FIGURE_SDF_CHUNK}
  vec3 coverFit(vec2 uv, float asp) {
    vec2 s = asp >= 1.0 ? vec2(1.0, 1.0 / asp) : vec2(asp, 1.0);
    vec2 c = (uv - 0.5) * s + 0.5;
    vec3 t = texture2D(uCover, c).rgb;
    return uHasCover > 0.5 ? t : mix(uPal0, uPal1, uv.y) * 0.5;
  }
  vec3 titleOn(vec2 uv, float asp, vec3 bg, float scale, float yc) {
    float th = scale * asp / uTitleAsp;
    vec2 t = vec2((uv.x - 0.5) / scale + 0.5, (uv.y - yc) / th + 0.5);
    if (t.x < 0.0 || t.x > 1.0 || t.y < 0.0 || t.y > 1.0) return bg;
    float a = texture2D(uTitle, t).r;
    return mix(bg, mix(uPal2, vec3(1.0), 0.25) * 0.82, a);
  }
  vec3 digitOn(vec2 uv, float asp, vec3 bg) {
    float h = 0.72, w = h / asp * 0.75;
    vec2 t = vec2((uv.x - 0.5) / w + 0.5, (uv.y - 0.58) / h + 0.5);
    if (uCount < 0.5 || t.x < 0.0 || t.x > 1.0 || t.y < 0.0 || t.y > 1.0) return bg;
    float a = texture2D(uDigits, vec2((t.x + uCount - 1.0) / 4.0, t.y)).r;
    return mix(bg, vec3(0.85), a);
  }
  vec3 camOn(vec2 uv, float asp, float px) {
    vec2 p = vec2((uv.x - 0.5) * asp, uv.y) * 1.05 + vec2(0.0, 0.95);
    vec3 bg = mix(uPal0 * 0.35, uPal1 * 0.55, uv.y) * (0.85 + 0.15 * step(0.5, fract(uv.y * 90.0)));
    vec3 d = figureSDF(p, uDJ, vec4(0.0, 1.0, 0.0, 0.4));
    float cov = clamp(0.5 - d.x / px, 0.0, 1.0);
    float rim = (1.0 - smoothstep(0.0, 4.0 * px, -d.x)) * clamp(d.z, 0.0, 1.0);
    return mix(bg, vec3(0.01) + uPal2 * rim * 0.9, cov);
  }
  vec2 contentUV(int panel, vec2 uv) {
    if (panel == 1) return uSideMap < 0.5 ? vec2(uv.x * ${SPAN[0].toFixed(4)}, uv.y) : vec2(mix(${SPAN[1].toFixed(4)}, ${SPAN[0].toFixed(4)}, uv.x), uv.y);
    if (panel == 2) return uSideMap < 0.5 ? vec2(${SPAN[1].toFixed(4)} + uv.x * ${SPAN[0].toFixed(4)}, uv.y) : vec2(mix(${SPAN[1].toFixed(4)}, ${SPAN[0].toFixed(4)}, uv.x), uv.y);
    if (panel == 3) return vec2(uv.x, 0.5);
    return vec2(mix(${SPAN[0].toFixed(4)}, ${SPAN[1].toFixed(4)}, uv.x), uv.y);
  }
  vec3 panelColor(int panel, vec2 uv, float asp, float px) {
    vec4 m = panel == 0 ? uModes[0] : (panel == 1 ? uModes[1] : (panel == 2 ? uModes[2] : uModes[3]));
    vec3 c = vec3(0.0);
    vec3 content = texture2D(uContent, contentUV(panel, uv)).rgb;
    c += pow(content, vec3(1.3)) * m.x * 2.6;
    if (m.y > 0.001) c += coverFit(uv, asp) * 0.8 * m.y;
    if (m.z > 0.001) {
      vec3 bg = content * 0.25;
      vec3 t = uCount > 0.5 && panel == 0 ? titleOn(uv, asp, digitOn(uv, asp, bg), 0.62, 0.13) : titleOn(uv, asp, bg, 0.92, 0.5);
      c += t * m.z;
    }
    if (m.w > 0.001) c += camOn(uv, asp, px) * m.w;
    return c;
  }
`;

const screenVertex = `
  precision highp float;
  ${SAFE_ZONE_VERTEX_CHUNK}
  varying vec2 vUv;
  varying float vSafe;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    vSafe = safeZoneAttenuation(gl_Position);
  }
`;
const screenFragment = `
  precision highp float;
  uniform float uPanel;
  uniform float uAsp;
  uniform vec2 uCells;
  uniform float uBright;
  ${PANEL_CHUNK}
  varying vec2 vUv;
  varying float vSafe;
  void main() {
    int panel = int(uPanel + 0.5);
    vec2 fw = fwidth(vUv);
    float px = max(fw.x * uAsp, fw.y) * 1.05;
    vec3 c = panelColor(panel, vUv, uAsp, px);
    // the LED grain: visible only when a cell is several pixels wide on screen
    vec2 cellsPx = 1.0 / max(fw * uCells, vec2(1e-5));
    float contrast = smoothstep(3.0, 7.0, min(cellsPx.x, cellsPx.y));
    vec2 g = fract(vUv * uCells) - 0.5;
    float led = 1.0 - smoothstep(0.28, 0.5, length(g));
    c *= mix(1.0, 0.25 + 0.95 * led, contrast * 0.9);
    c *= uBright;
    float mx = max(c.r, max(c.g, c.b));
    if (mx > 0.6) c *= (0.6 + 0.22 * (1.0 - exp(-(mx - 0.6) / 0.22))) / mx;
    // the frame's black border
    vec2 e = min(vUv, 1.0 - vUv) / max(fw, vec2(1e-5));
    c *= smoothstep(1.0, 3.0, min(e.x, e.y));
    gl_FragColor = vec4(c * vSafe, 1.0);
  }
`;
// the zones: 8 across the three screens, each the mean of 12 samples
const zonesFragment = `
  precision highp float;
  ${PANEL_CHUNK}
  uniform float uBright;
  varying vec2 vUv;
  void main() {
    float z = floor(vUv.x * ${ZONES.toFixed(1)});
    int panel; float u0, u1, asp;
    if (z < 1.5) { panel = 1; u0 = z * 0.5; u1 = u0 + 0.5; asp = ${(SCREEN.side.w / SCREEN.side.h).toFixed(3)}; }
    else if (z > 5.5) { panel = 2; u0 = (z - 6.0) * 0.5; u1 = u0 + 0.5; asp = ${(SCREEN.side.w / SCREEN.side.h).toFixed(3)}; }
    else { panel = 0; u0 = (z - 2.0) * 0.25; u1 = u0 + 0.25; asp = ${(SCREEN.centre.w / SCREEN.centre.h).toFixed(3)}; }
    vec3 s = vec3(0.0);
    for (int i = 0; i < 4; i++) for (int j = 0; j < 3; j++) s += panelColor(panel, vec2(mix(u0, u1, (float(i) + 0.5) / 4.0), (float(j) + 0.5) / 3.0), asp, 0.02);
    gl_FragColor = vec4(s / 12.0 * uBright, 1.0);
  }
`;

function canvasTex(w, h) {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (c) { c.width = w; c.height = h; }
  const t = c ? new THREE.CanvasTexture(c) : new THREE.DataTexture(new Uint8Array(4), 1, 1);
  t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = true;
  return { c, t };
}

export class StageScreens {
  constructor(scene, opts = {}) {
    this._scene = scene;
    this._tier = opts.tier != null ? opts.tier : 1;
    // the content: the generators
    this.bus = {
      uRes: { value: new THREE.Vector2(CW, CH) }, uAspect: { value: CW / CH }, uTime: { value: 0 }, uBeats: { value: 0 }, uBeatPhase: { value: 0 }, uBarPhase: { value: 0 },
      uPulse: { value: 0 }, uBarPulse: { value: 0 }, uEnergy: { value: 0.5 }, uSeed: { value: 0.3 }, uBarIndex: { value: 0 }, uBands: { value: new THREE.Vector3(0.5, 0.4, 0.3) },
      uCol0: { value: new THREE.Color(0xff3c8c) }, uCol1: { value: new THREE.Color(0x3cc8ff) }, uCol2: { value: new THREE.Color(0xffd23c) }, uCover: { value: null }, uHasCover: { value: 0 }
    };
    const frags = { tunnel: MIXDECK_BG.tunnel, plasma: MIXDECK_BG.plasma, rays: MIXDECK_BG.rays, nebula: MIXDECK_BG.nebula, spectrum: spectrumFragment, shapes: shapesFragment };
    this._gen = {};
    for (const k of GENERATORS) this._gen[k] = new THREE.ShaderMaterial({ uniforms: this.bus, vertexShader: MIXDECK_QUAD, fragmentShader: frags[k], depthTest: false, depthWrite: false });
    this._passScene = new THREE.Scene();
    this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._gen.tunnel);
    this._passQuad.frustumCulled = false;
    this._passScene.add(this._passQuad);
    this._ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._rtContent = new THREE.WebGLRenderTarget(CW, CH, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    this._rtZones = new THREE.WebGLRenderTarget(ZONES, 1, { depthBuffer: false, stencilBuffer: false, type: THREE.HalfFloatType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    this.zonesTexture = this._rtZones.texture;
    this.generator = 'tunnel';
    // title and digits
    this._title = canvasTex(1024, 256);
    this._digits = canvasTex(1024, 256);
    this._drawDigits();
    this.setTitle('');
    // the panel uniforms (shared)
    const djv = []; for (let k = 0; k < JOINTS; k++) djv.push(new THREE.Vector2());
    this.panelU = {
      uContent: { value: this._rtContent.texture }, uCover: { value: null }, uHasCover: { value: 0 }, uTitle: { value: this._title.t }, uTitleAsp: { value: 4 },
      uDigits: { value: this._digits.t }, uCount: { value: 0 }, uModes: { value: [0, 1, 2, 3].map(() => new THREE.Vector4(1, 0, 0, 0)) }, uSideMap: { value: 0 },
      uDJ: { value: djv }, uPal0: this.bus.uCol0, uPal1: this.bus.uCol1, uPal2: this.bus.uCol2, uTime: this.bus.uTime, uBright: { value: 1 }
    };
    // the panels
    this._meshes = [];
    const mk = (w, h, panel, cells) => {
      const u = Object.assign({}, safeZoneUniforms, this.panelU, { uPanel: { value: panel }, uAsp: { value: w / h }, uCells: { value: new THREE.Vector2(cells[0], cells[1]) } });
      const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: screenVertex, fragmentShader: '#define SCREEN\n' + screenFragment, extensions: { derivatives: true } });
      const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
      m.renderOrder = 0;
      scene.add(m); this._meshes.push(m);
      return m;
    };
    const C = SCREEN.centre, S = SCREEN.side, B = SCREEN.band;
    const centre = mk(C.w, C.h, 0, [320, 180]); centre.position.set(C.x, C.y, C.z);
    const left = mk(S.w, S.h, 1, [108, 180]); left.position.set(-S.x, S.y, S.z); left.rotation.y = S.turn;
    const right = mk(S.w, S.h, 2, [108, 180]); right.position.set(S.x, S.y, S.z); right.rotation.y = -S.turn;
    const band = mk(B.w, B.h, 3, [600, 5]); band.position.set(0, B.y, B.z);
    this._zonesMat = new THREE.ShaderMaterial({ uniforms: this.panelU, vertexShader: MIXDECK_QUAD, fragmentShader: zonesFragment, depthTest: false, depthWrite: false });
    // the passes run before the centre screen draws
    const self = this;
    centre.onBeforeRender = function (renderer) { self._render(renderer); };
    this.centre = centre;
  }
  _drawDigits() {
    const { c, t } = this._digits;
    if (!c) return;
    const g = c.getContext('2d');
    g.clearRect(0, 0, c.width, c.height);
    g.fillStyle = '#000'; g.fillRect(0, 0, c.width, c.height);
    g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = '900 230px "Arial Black", Impact, sans-serif';
    for (let i = 0; i < 4; i++) g.fillText(String(i + 1), (i + 0.5) * 256, 132);
    t.needsUpdate = true;
  }
  setTitle(text) {
    const { c, t } = this._title;
    this._titleText = text;
    if (!c) return;
    const g = c.getContext('2d');
    g.fillStyle = '#000'; g.fillRect(0, 0, c.width, c.height);
    const s = String(text || '').toUpperCase().slice(0, 32);
    g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle';
    let size = 190;
    g.font = `900 ${size}px "Arial Black", Impact, sans-serif`;
    const wmax = c.width * 0.96;
    const mw = g.measureText(s).width;
    if (mw > wmax) { size = Math.max(60, Math.floor(size * wmax / mw)); g.font = `900 ${size}px "Arial Black", Impact, sans-serif`; }
    g.fillText(s, c.width / 2, c.height / 2 + size * 0.05);
    t.needsUpdate = true;
  }
  setCover(texture) { this.panelU.uCover.value = texture; this.panelU.uHasCover.value = texture ? 1 : 0; this.bus.uCover.value = texture; this.bus.uHasCover.value = texture ? 1 : 0; }
  setPalette(c0, c1, c2) { this.bus.uCol0.value.copy(c0); this.bus.uCol1.value.copy(c1); this.bus.uCol2.value.copy(c2); }
  // modes: per panel [content, cover, title, camera]
  setMode(panel, v) { this.panelU.uModes.value[panel].set(v[0], v[1], v[2], v[3]); }
  setDJ(J) { for (let k = 0; k < JOINTS; k++) this.panelU.uDJ.value[k].set(J[k * 2], J[k * 2 + 1]); }
  _render(renderer) {
    const cur = renderer.getRenderTarget();
    this._passQuad.material = this._gen[this.generator] || this._gen.tunnel;
    renderer.setRenderTarget(this._rtContent); renderer.render(this._passScene, this._ortho);
    this._passQuad.material = this._zonesMat;
    renderer.setRenderTarget(this._rtZones); renderer.render(this._passScene, this._ortho);
    renderer.setRenderTarget(cur);
  }
  // compile every generator once (the first frames)
  precompile(renderer) {
    const cur = renderer.getRenderTarget();
    for (const k of GENERATORS) { this._passQuad.material = this._gen[k]; renderer.setRenderTarget(this._rtContent); renderer.render(this._passScene, this._ortho); }
    renderer.setRenderTarget(cur);
  }
  dispose() {
    for (const m of this._meshes) { this._scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
    this.centre.onBeforeRender = null;
    for (const k in this._gen) this._gen[k].dispose();
    this._zonesMat.dispose(); this._passQuad.geometry.dispose();
    this._rtContent.dispose(); this._rtZones.dispose();
    this._title.t.dispose(); this._digits.t.dispose();
  }
}
